import React, { useEffect, useRef, useState } from "react";
import { WS_BASE } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Broadcast, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * LiveLocationSharer
 * - Subscribes to /api/ws/location/{activity_id}?token=...
 * - Receives `snapshot` then real-time `location` updates
 * - When sharing is enabled, watchPosition() pushes updates upstream
 * - Calls onUpdate({ user_id, lat, lng, ... }[]) so parent can render markers
 */
export default function LiveLocationSharer({ activityId, onUpdate }) {
  const { t } = useI18n();
  const [sharing, setSharing] = useState(false);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const watchIdRef = useRef(null);
  const livePositionsRef = useRef({}); // user_id -> {lat, lng, ...}

  useEffect(() => {
    const token = localStorage.getItem("tf_token") || "";
    if (!token) return;
    const ws = new WebSocket(`${WS_BASE}/api/ws/location/${activityId}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "snapshot" && Array.isArray(msg.locations)) {
          for (const l of msg.locations) livePositionsRef.current[l.user_id] = l;
        } else if (msg.type === "location") {
          livePositionsRef.current[msg.user_id] = msg;
        } else if (msg.type === "leave") {
          delete livePositionsRef.current[msg.user_id];
        }
        onUpdate?.(Object.values(livePositionsRef.current));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("location ws bad payload", err?.message);
      }
    };
    return () => {
      try { ws.close(); } catch (err) {
        // eslint-disable-next-line no-console
        console.debug("location ws close failed", err?.message);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId]);

  const start = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === 1) {
          ws.send(JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude }));
        }
      },
      (err) => { toast.error(err.message || "Location error"); stop(); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    watchIdRef.current = id;
    setSharing(true);
  };

  const stop = () => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setSharing(false);
  };

  useEffect(() => () => stop(), []);

  return (
    <div className="flex items-center gap-2">
      {sharing ? (
        <button data-testid="stop-location-btn" onClick={stop} className="kinetic-btn border border-[#FF3B30]/50 hover:bg-[#FF3B30]/10 text-[#FF3B30] px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold">
          <X weight="bold" size={14} /> {t("activity.stop_sharing")}
        </button>
      ) : (
        <button data-testid="share-location-btn" onClick={start} className="kinetic-btn bg-[#34C759] hover:bg-[#2ba84b] text-black px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold">
          <Broadcast weight="bold" size={14} /> {t("activity.share_location")}
        </button>
      )}
      <span className={`label-overline ${connected ? "text-[#34C759]" : ""}`}>
        {connected ? t("activity.live_location") : "…"}
      </span>
    </div>
  );
}
